import {
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    styled,
} from "@mui/material";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import DeleteIcon from "@mui/icons-material/Delete";
import { Question } from "components/Admin/Organizations/types/Module.type";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import {
    LicensedModuleQuestionDependencyConditionalGroup,
    QuestionConditionalGroupType,
    QuestionDependencyQuestionAnswerCondition,
    QuestionDependencyQuestionAnswerOperator,
    QuestionDependencyType,
} from "models/Question";
import React from "react";
import { emptyGUID } from "./OrganizationModuleQuestionDependencies";
import OrganizationModuleQuestionSubDependency from "./OrganizationModuleQuestionSubDependency";

const StyledDeleteIcon = styled(DeleteIcon)(({ theme }) => ({
    cursor: "pointer",
    color: theme.palette.blue.main,
    "&:hover": {
        transform: "scale(1.03)",
    },
}));

const StyledAddIcon = styled(AddSharpIcon)(({ theme }) => ({
    cursor: "pointer",
    color: theme.palette.gray.main,
    "&:hover": {
        transform: "scale(1.03)",
    },
}));



type Props = {
    conditionIndex: number;
    condition: any;
    index: number;
    value: Question;
    onChange: (key: keyof Question, val: any) => void;
    dependencyQuestions: Question[];
    isDefault?: boolean;
};

const questionDepencyOptions = [
    {
        id: 1,
        label: "Condition Group",
    },
    {
        id: 2,
        label: "Question/Answer",
    },
];

const IsAnsweredQuestionOperatorId = 2;

export const questionOperatorOptions = [
    {
        id: 1,
        label: "Contains",
    },
    {
        id: IsAnsweredQuestionOperatorId,
        label: "IsAnswered",
    },
];



const OrganizationModuleQuestionDependency: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({
    conditionIndex,
    condition,
    index,
    value,
    onChange,
    dependencyQuestions,
    isDefault,
}) => {
    const changeDependencyType = (id: number, conditionIndex: number) => {
        const previousSetup = JSON.parse(JSON.stringify(value.dependencies));
        const previousSetupConditions = previousSetup.conditions;

        if (id === QuestionDependencyType.QuestionAnswer) {
            previousSetupConditions[conditionIndex] = {
                dependsOnQuestionId: emptyGUID,
                op: QuestionDependencyQuestionAnswerOperator.Contains,
                value: "",
                type: QuestionDependencyType.QuestionAnswer,
            } as QuestionDependencyQuestionAnswerCondition;
        } else if (id === QuestionDependencyType.ConditionalGroup) {
            previousSetupConditions[conditionIndex] = {
                conditions: [
                    {
                        dependsOnQuestionId: emptyGUID,
                        op: QuestionDependencyQuestionAnswerOperator.IsAnswered,
                        value: "",
                        type: QuestionDependencyType.QuestionAnswer,
                    } as QuestionDependencyQuestionAnswerCondition,
                ],
                conditionalGroupType: QuestionConditionalGroupType.Or,
                type: QuestionDependencyType.ConditionalGroup,
            } as LicensedModuleQuestionDependencyConditionalGroup;
        }
        return previousSetup;
    };

    const changeQuestion = (id: string, conditionIndex: number) => {
        const previousDependencies = JSON.parse(
            JSON.stringify(value.dependencies),
        );

        previousDependencies.conditions[conditionIndex].dependsOnQuestionId =
            id;

        previousDependencies.conditions[conditionIndex].value = "";

        return previousDependencies;
    };

    const changeTag = (tagValue: string, conditionIndex: number) => {
        const previousDependencies = JSON.parse(
            JSON.stringify(value.dependencies),
        );

        const clickedCondition = previousDependencies.conditions[
            conditionIndex
        ] as QuestionDependencyQuestionAnswerCondition;

        clickedCondition.value = tagValue;

        return previousDependencies;
    };

    const changeOperator = (operatorId: number, conditionIndex: number) => {
        const previousDependencies = JSON.parse(
            JSON.stringify(value.dependencies),
        );

        const clickedCondition = previousDependencies.conditions[
            conditionIndex
        ] as QuestionDependencyQuestionAnswerCondition;

        clickedCondition.op = operatorId;

        if (operatorId === IsAnsweredQuestionOperatorId) {
            clickedCondition.value = "";
        }

        return previousDependencies;
    };

    const dependencyTypeDropdown = (conditionIndex: number) => {
        const defaultValueId =
            value.dependencies?.conditions[conditionIndex].type;

        return (
            <AcxSelectSingle
                inputLabel="Type"
                id={`questionDecisionType-${index}`}
                valueField="id"
                labelField="label"
                options={questionDepencyOptions}
                defaultValue={{
                    id: defaultValueId!,
                    label: questionDepencyOptions[defaultValueId! - 1]?.label,
                }}
                onChange={(val) => {
                    onChange(
                        "dependencies",
                        changeDependencyType(val.id, conditionIndex),
                    );
                }}
                isDisabled={isDefault}
            />
        );
    };

    const addBinaryDependency = (conditionIndex: number) => {
        const previousDependencies = JSON.parse(
            JSON.stringify(value.dependencies),
        );
        previousDependencies?.conditions[conditionIndex].conditions.push({
            dependsOnQuestionId: emptyGUID,
            op: QuestionDependencyQuestionAnswerOperator.Contains,
            value: "",
            type: QuestionDependencyType.QuestionAnswer,
        } as QuestionDependencyQuestionAnswerCondition);
        return previousDependencies;
    };

    const removeDependency = (conditionIndex: number) => {
        const previousDependencies = JSON.parse(
            JSON.stringify(value.dependencies),
        );

        previousDependencies.conditions.splice(conditionIndex, 1);

        return previousDependencies;
    };

    const questionDropdownDefault = dependencyQuestions?.find((q) => {
        const depCondition = value?.dependencies?.conditions[
            conditionIndex
        ] as QuestionDependencyQuestionAnswerCondition;
        return q.id === depCondition.dependsOnQuestionId;
    });

    const containsDropdownDefault = questionDropdownDefault?.tags?.find((t) => {
        const depCondition = value?.dependencies?.conditions[
            conditionIndex
        ] as QuestionDependencyQuestionAnswerCondition;
        return t.value === depCondition.value;
    });

    const changeRadio = (groupType: string, conditionIndex: number) => {
        const previousDependencies = JSON.parse(
            JSON.stringify(value.dependencies),
        );

        previousDependencies.conditions[conditionIndex].conditionalGroupType =
            QuestionConditionalGroupType[groupType];

        return previousDependencies;
    };

    const conditionalGroupType =
        QuestionConditionalGroupType[
            (
                value.dependencies?.conditions[
                    conditionIndex
                ] as LicensedModuleQuestionDependencyConditionalGroup
            ).conditionalGroupType
        ];

    const operator = (
        value.dependencies?.conditions[
            conditionIndex
        ] as QuestionDependencyQuestionAnswerCondition
    ).op;

    return (
        <Grid
            container
            key={conditionIndex}
            sx={{
                justifyContent: "space-between",
                alignItems: "flex-end",
                paddingTop: "8px",
            }}
        >
            {condition.type === QuestionDependencyType.ConditionalGroup ? (
                <>
                    <Grid item xs={3}>
                        {dependencyTypeDropdown(conditionIndex)}
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                aria-label="conditional-type"
                                value={conditionalGroupType}
                                name="radio-buttons-group"
                                onChange={(event) => {
                                    onChange(
                                        "dependencies",
                                        changeRadio(
                                            event.currentTarget.value,
                                            conditionIndex,
                                        ),
                                    );
                                }}
                            >
                                <FormControlLabel
                                    value="And"
                                    control={<Radio disabled={isDefault} />}
                                    label="All are true"
                                />
                                <FormControlLabel
                                    value="Or"
                                    control={<Radio disabled={isDefault} />}
                                    label="Any are true"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        {!isDefault && (
                            <>
                                <StyledAddIcon
                                    onClick={() => {
                                        onChange(
                                            "dependencies",
                                            addBinaryDependency(conditionIndex),
                                        );
                                    }}
                                />
                                <StyledDeleteIcon
                                    onClick={() =>
                                        onChange(
                                            "dependencies",
                                            removeDependency(conditionIndex),
                                        )
                                    }
                                />{" "}
                            </>
                        )}
                    </Grid>

                    <Grid
                        container
                        item
                        xs={12}
                        sx={{ marginTop: "8px" }}
                    >
                        {condition.conditions.map(
                            (_binaryCond, groupIndex: number) => (
                                <OrganizationModuleQuestionSubDependency
                                    key={
                                        (value.id ?? "") +
                                        conditionIndex +
                                        groupIndex
                                    }
                                    conditionIndex={conditionIndex}
                                    value={value}
                                    index={index}
                                    onChange={onChange}
                                    groupIndex={groupIndex}
                                    dependencyQuestions={dependencyQuestions}
                                    isDefault={isDefault}
                                />
                            ),
                        )}
                    </Grid>
                </>
            ) : (
                <>
                    <Grid item xs={2}>
                        {dependencyTypeDropdown(conditionIndex)}
                    </Grid>
                    <Grid item xs={2}>
                        <AcxSelectSingle
                            inputLabel="Question"
                            id={`questionDecisionType-${index}`}
                            valueField="id"
                            labelField="questionText"
                            options={dependencyQuestions.filter(
                                (q) => q.id !== value.id,
                            )}
                            defaultValue={questionDropdownDefault}
                            onChange={(val) => {
                                onChange(
                                    "dependencies",
                                    changeQuestion(
                                        val.id ?? "",
                                        conditionIndex,
                                    ),
                                );
                            }}
                            required
                            isDisabled={isDefault}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        {questionDropdownDefault && (
                            <AcxSelectSingle
                                inputLabel="Operator"
                                id={`operatorList-${index}`}
                                valueField="id"
                                labelField="label"
                                options={questionOperatorOptions}
                                defaultValue={{
                                    id: operator,
                                    label: questionOperatorOptions[operator - 1]
                                        ?.label,
                                }}
                                onChange={(val) => {
                                    onChange(
                                        "dependencies",
                                        changeOperator(
                                            val.id ?? 0,
                                            conditionIndex,
                                        ),
                                    );
                                }}
                                required
                                isDisabled={isDefault}
                            />
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        {questionDropdownDefault &&
                            operator ===
                                QuestionDependencyQuestionAnswerOperator.Contains && (
                                <AcxSelectSingle
                                    inputLabel="Answer"
                                    id={`containList-${index}`}
                                    valueField="id"
                                    labelField="value"
                                    options={
                                        questionDropdownDefault?.tags ?? []
                                    }
                                    defaultValue={containsDropdownDefault}
                                    onChange={(val) => {
                                        onChange(
                                            "dependencies",
                                            changeTag(
                                                val.value ?? "",
                                                conditionIndex,
                                            ),
                                        );
                                    }}
                                    required
                                    isDisabled={isDefault}
                                />
                            )}
                    </Grid>

                    {!isDefault && (
                        <StyledDeleteIcon
                            onClick={() =>
                                onChange(
                                    "dependencies",
                                    removeDependency(conditionIndex),
                                )
                            }
                        />
                    )}
                </>
            )}
        </Grid>
    );
};

export default OrganizationModuleQuestionDependency;
